<template>
  <!--
    SessionCountsDisplayDialog.vue

    Large display panel for session attendance and registration counts.
    Features:
      - Big number display for easy visibility
      - Tabs to switch between Registration and Attendance counts
      - Auto-refresh functionality with configurable intervals
      - Manual refresh controls
      - Full-screen display option
  -->
  <v-dialog v-model="dialog" persistent fullscreen transition="dialog-bottom-transition">
    <v-card v-if="session">
      <v-app-bar dark color="primary">
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ session.name }} - Live Counts</v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Auto-refresh controls -->
        <div class="d-flex align-center mr-4">
          <v-select
            v-model="refreshInterval"
            :items="refreshIntervals"
            label="Auto Refresh"
            dense
            outlined
            hide-details
            style="max-width: 150px;"
            class="mr-2"
            @change="updateAutoRefresh"
          ></v-select>

          <v-btn
            :color="autoRefreshActive ? 'success' : 'default'"
            :outlined="!autoRefreshActive"
            class="mr-2"
            @click="toggleAutoRefresh"
          >
            <v-icon left>{{ autoRefreshActive ? 'mdi-pause' : 'mdi-play' }}</v-icon>
            {{ autoRefreshActive ? 'Pause' : 'Start' }}
          </v-btn>

          <v-btn
            :color="fullScreenMode ? 'warning' : 'default'"
            :outlined="!fullScreenMode"
            class="mr-2"
            @click="toggleFullScreenMode"
          >
            <v-icon left>{{ fullScreenMode ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            {{ fullScreenMode ? 'Exit Focus' : 'Focus Mode' }}
          </v-btn>

          <v-btn
            color="white"
            outlined
            :loading="sessionCountsLoading"
            @click="manualRefresh"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </div>
      </v-app-bar>

      <v-card-text class="pa-0">
        <!-- Focus Mode - Ultra large display -->
        <div v-if="fullScreenMode" class="focus-mode-container">
          <v-container fluid class="fill-height d-flex align-center justify-center">
            <div class="text-center">
              <h1 class="focus-number font-weight-bold mb-8">
                {{ getCurrentDisplayCount }}
              </h1>
              <h2 class="focus-subtitle text--secondary">{{ getCurrentDisplaySubtitle }}</h2>
            </div>
          </v-container>
        </div>

        <!-- Normal Mode - Full interface -->
        <div v-else>
          <!-- Session Information Header -->
          <v-container class="py-4" style="background-color: #f5f5f5;">
            <v-row>
              <v-col cols="12" class="text-center">
                <h2 class="text-h4 mb-2">{{ session.name }}</h2>
                <p class="text-h6 text--secondary mb-1">{{ session.description }}</p>
                <p class="text-body-1 text--secondary">
                  {{ session.dateStart | readableDateTime }} - {{ session.dateEnd | readableDateTime }}
                </p>
                <p class="text-body-1 text--secondary">
                  Registration Limit: <span v-if="session.limit">{{ session.limit }} participants</span><span v-else>No limit</span>
                </p>
              </v-col>
            </v-row>
          </v-container>

          <!-- Tabs for switching between counts -->
          <v-tabs v-model="activeTab" centered grow>
            <v-tab v-if="!isRegistrationSession" key="registration">
              <v-icon left>mdi-account-plus</v-icon>
              Registration Count
            </v-tab>
            <v-tab key="attendance">
              <v-icon left>mdi-account-check</v-icon>
              Attendance Count
            </v-tab>
          </v-tabs>

          <!-- Tab content -->
          <v-tabs-items v-model="activeTab">
            <!-- Registration Count Tab -->
            <v-tab-item v-if="!isRegistrationSession" key="registration">
              <v-container fluid class="fill-height d-flex align-center justify-center" style="min-height: 500px;">
                <div class="text-center">
                  <h1 class="display-4 font-weight-bold mb-4" style="font-size: 8rem !important;">
                    {{ sessionCounts ? sessionCounts.registrantsCount : 0 }}
                  </h1>
                  <h2 class="text-h3 text--secondary mb-4">Registered Participants</h2>
                </div>
              </v-container>
            </v-tab-item>

            <!-- Attendance Count Tab -->
            <v-tab-item key="attendance">
              <v-container fluid class="fill-height d-flex align-center justify-center" style="min-height: 500px;">
                <div class="text-center">
                  <h1 class="display-4 font-weight-bold mb-4" style="font-size: 8rem !important;">
                    {{ sessionCounts ? sessionCounts.attendanceCount : 0 }}
                  </h1>
                  <h2 class="text-h3 text--secondary mb-4">Attended Participants</h2>
                </div>
              </v-container>
            </v-tab-item>
          </v-tabs-items>

          <!-- Status Information -->
          <v-container class="py-4" style="background-color: #f5f5f5;">
            <v-row>
              <v-col cols="12" md="4" class="text-center">
                <v-card class="pa-4 mx-2" elevation="2">
                  <h3 class="text-h5 mb-2">Last Updated</h3>
                  <p class="text-body-1 text--secondary">
                    {{ lastUpdated | readableDateTime }}
                  </p>
                </v-card>
              </v-col>
              <v-col cols="12" md="4" class="text-center">
                <v-card class="pa-4 mx-2" elevation="2">
                  <h3 class="text-h5 mb-2">Auto Refresh</h3>
                  <p class="text-body-1" :class="autoRefreshActive ? 'success--text' : 'text--secondary'">
                    {{ autoRefreshActive ? `Every ${refreshInterval} seconds` : 'Disabled' }}
                  </p>
                </v-card>
              </v-col>
              <v-col cols="12" md="4" class="text-center">
                <v-card class="pa-4 mx-2" elevation="2">
                  <h3 class="text-h5 mb-2">Next Refresh</h3>
                  <p class="text-body-1 text--secondary">
                    {{ autoRefreshActive ? `${timeUntilNextRefresh}s` : 'Manual only' }}
                  </p>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { format } from 'date-fns';

export default {
  name: 'SessionCountsDisplayDialog',
  filters: {
    /**
     * Converts a date string to a human-readable date time format.
     * @param {string} value - The date string.
     * @returns {string} Formatted date and time.
     */
    readableDateTime: (value) => {
      if (!value) return '';
      return format(new Date(value), 'MMM d, y h:mm a');
    },
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    session: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeTab: 0,
      fullScreenMode: false,
      refreshInterval: 30,
      refreshIntervals: [
        { text: 'Disabled', value: 0 },
        { text: '10 seconds', value: 10 },
        { text: '30 seconds', value: 30 },
        { text: '1 minute', value: 60 },
        { text: '2 minutes', value: 120 },
        { text: '5 minutes', value: 300 },
      ],
      autoRefreshActive: false,
      autoRefreshTimer: null,
      lastUpdated: new Date(),
      timeUntilNextRefresh: 0,
      countdownTimer: null,
    };
  },
  computed: {
    ...mapState(['sessionCounts', 'sessionCountsLoading']),

    /**
     * Check if this is a "Registration" session
     * @returns {boolean} True if session name equals "Registration"
     */
    isRegistrationSession() {
      return this.session && this.session.name === 'Registration';
    },

    /**
     * Calculate registration progress percentage
     * @returns {number} Progress percentage (0-100)
     */
    registrationProgress() {
      if (!this.sessionCounts || !this.session) return 0;
      return Math.min((this.sessionCounts.registrantsCount / this.session.limit) * 100, 100);
    },

    /**
     * Calculate attendance progress percentage (based on registered count)
     * @returns {number} Progress percentage (0-100)
     */
    attendanceProgress() {
      if (!this.sessionCounts || this.sessionCounts.registrantsCount === 0) return 0;
      return Math.min((this.sessionCounts.attendanceCount / this.sessionCounts.registrantsCount) * 100, 100);
    },

    /**
     * Get the current display count based on active tab
     * @returns {number} Current count to display
     */
    getCurrentDisplayCount() {
      if (!this.sessionCounts) return 0;
      if (this.isRegistrationSession) {
        return this.sessionCounts.attendanceCount || 0;
      }
      if (this.activeTab === 0 && !this.isRegistrationSession) {
        return this.sessionCounts.registrantsCount || 0;
      }
      return this.sessionCounts.attendanceCount || 0;
    },

    /**
     * Get the current display subtitle based on active tab
     * @returns {string} Current subtitle to display
     */
    getCurrentDisplaySubtitle() {
      if (this.isRegistrationSession) {
        return 'Attended Participants';
      }
      if (this.activeTab === 0 && !this.isRegistrationSession) {
        return 'Registered Participants';
      }
      return 'Attended Participants';
    },
  },
  watch: {
    /**
     * Watch for dialog changes to load session counts
     */
    dialog(newValue) {
      if (newValue && this.session) {
        // Set default tab - when registration tab is hidden, attendance will be at index 0
        this.activeTab = 0;
        this.loadSessionCounts();
        if (this.refreshInterval > 0) {
          this.startAutoRefresh();
        }
      } else {
        this.stopAutoRefresh();
      }
    },
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    ...mapActions(['getSessionCountsAction']),

    /**
     * Loads session counts
     */
    async loadSessionCounts() {
      if (this.session?.id) {
        await this.getSessionCountsAction(this.session.id);
        this.lastUpdated = new Date();
      }
    },

    /**
     * Manual refresh of counts
     */
    async manualRefresh() {
      await this.loadSessionCounts();
    },

    /**
     * Update auto refresh when interval changes
     */
    updateAutoRefresh() {
      if (this.refreshInterval === 0) {
        this.stopAutoRefresh();
      } else if (this.autoRefreshActive) {
        this.stopAutoRefresh();
        this.startAutoRefresh();
      }
    },

    /**
     * Toggle auto refresh on/off
     */
    toggleAutoRefresh() {
      if (this.autoRefreshActive) {
        this.stopAutoRefresh();
      } else if (this.refreshInterval > 0) {
        this.startAutoRefresh();
      }
    },

    /**
     * Start auto refresh timer
     */
    startAutoRefresh() {
      if (this.refreshInterval <= 0) return;

      this.autoRefreshActive = true;
      this.timeUntilNextRefresh = this.refreshInterval;

      // Start countdown timer
      this.countdownTimer = setInterval(() => {
        this.timeUntilNextRefresh -= 1;
        if (this.timeUntilNextRefresh <= 0) {
          this.timeUntilNextRefresh = this.refreshInterval;
        }
      }, 1000);

      // Start refresh timer
      this.autoRefreshTimer = setInterval(() => {
        this.loadSessionCounts();
      }, this.refreshInterval * 1000);
    },

    /**
     * Stop auto refresh timer
     */
    stopAutoRefresh() {
      this.autoRefreshActive = false;
      this.timeUntilNextRefresh = 0;

      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }

      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },

    /**
     * Toggle full screen focus mode
     */
    toggleFullScreenMode() {
      this.fullScreenMode = !this.fullScreenMode;
    },

    /**
     * Get progress bar color based on percentage
     * @param {number} percentage - Progress percentage
     * @returns {string} Color name
     */
    getProgressColor(percentage) {
      if (percentage >= 90) return 'error';
      if (percentage >= 75) return 'warning';
      if (percentage >= 50) return 'info';
      return 'success';
    },
  },
};
</script>

<style scoped>
.display-4 {
  line-height: 1 !important;
}

.v-tabs-items {
  background-color: transparent;
}

.fill-height {
  min-height: calc(100vh - 300px);
}

.focus-mode-container {
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.focus-number {
  font-size: 20rem !important;
  line-height: 0.8 !important;
  color: #1976d2;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.focus-subtitle {
  font-size: 4rem !important;
  font-weight: 300 !important;
  letter-spacing: 2px;
  text-transform: uppercase;
}

@media (max-width: 1200px) {
  .focus-number {
    font-size: 15rem !important;
  }

  .focus-subtitle {
    font-size: 3rem !important;
  }
}

@media (max-width: 768px) {
  .focus-number {
    font-size: 10rem !important;
  }

  .focus-subtitle {
    font-size: 2rem !important;
  }
}
</style>
